import { Row } from 'antd';
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Panel from '../../../../../../../shared/components/card/Panel';
import FormCustomMaskedInput from '../../../../../../../shared/components/crmFormElement/FormCustomMaskedInput/FormCustomMaskedInput';
import { FormInputMobile, FormSelect, FormTextInput } from '../../../../../../../shared/components/crmFormElement/FormInput';
import { AADHAR_VALIDATOR, GUTTOR_SPACE } from '../../../../../../../shared/constants/commonConstants';
import { useFindPayer } from '../../../../../../../shared/hooks/useFindPayer';
import { RootState } from '../../../../../../../store';
import { NewAdmissionState } from '../../../../../../../store/models/newAdmissionModel';
import { getLookUpFullDetail, handleUserFocus } from '../../../../service/crmUtils';
import { AdmissionContext } from '../../../AdmissionContext';
import CONTROL_CONFIG_BASED_ON_PAYER from '../../../../../../../config/formControlConfig';
import { formInputStates, OTHER_ID } from '../../../../../../../shared/constants/crmConstants';
import { disableRoomType, toggleOtherRoomType } from '../../../../../../../store/actions/newAdmissionAction';
import FormSwitch from '../../../../../../../shared/components/crmFormElement/FormSwitch/FormSwitch';

const OtherMemberDetailCard = ({ isLoading, form, isCertificateNoReq }: any): ReactElement | null => {
  const { isBeneficiarySelected } = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  const {
    newAdmission: { selectedBeneficiaryDetail = {},
    },
  } = useSelector((state: RootState): RootState => state);
  const { newAdmission: {selectedDraft},
  }: any = useSelector((state: RootState): RootState => state);
  const { admissionDetails: { claimDetails }, payerLookUp, payerList: { payer, payer: { uniqueIdentifier, name } } } =
    useSelector((state: RootState): RootState => state);
  const admissionContext = useContext(AdmissionContext);
  const dispatch = useDispatch();
  const { claimDetailBackup } = admissionContext;
  const [isAadharValid, setIsAadharValid] = useState(false);
  const [isOtherMediclaimRequired, setIsOtherMediclaimRequired] = useState(false);
  const [formElementCount, setFormElementCount] = useState([] as any);
  const { roomType: benefRoomType }: any = selectedBeneficiaryDetail || {};
  const { isDraftBtnClicked } = useSelector((state: RootState): any => state.selectedInPatientDetail);
  const [inputStates, setInputStates] = useState(formInputStates.otherMemberDetailsCard);

  let payerName = name;
  const findPayer = useFindPayer();
  if (claimDetails && !payerName) {
    const { payerId } = claimDetails;
    payerName = findPayer(payerId);
  }
  const payerConfig = CONTROL_CONFIG_BASED_ON_PAYER[payerName];
  const payerConfigValues = Object.values(payerConfig || []);
  const adharHidden = payerConfigValues?.filter(({ type }: any): boolean => type === 'VIDAL').length;
  useEffect((): any => {
    if (payerConfig) {
      const formElements = payerConfigValues?.filter(({ type, hidden }: any): boolean => type === 'member' && !hidden);
      setFormElementCount(formElements);
    }
  }, [payer]);

  useEffect((): any => {
    const aadharNumber = form.getFieldsValue(['aadharNo'])?.aadharNo || '';
    setIsAadharValid(AADHAR_VALIDATOR.test(aadharNumber));
  }, []);

  const commonDetail = { isLoading, form, claimDetailBackup };
  const { memberGenderSex, memberGenderSelect } = claimDetailBackup || {};
  claimDetailBackup.memberGenderSelect = memberGenderSex || memberGenderSelect;
  const parsedDraftData = selectedDraft?.rawDraft?.length > 0 ? JSON?.parse(selectedDraft?.rawDraft) : {};
  const isFormDisabled = (key: any): any => {
    if (isDraftBtnClicked) {
      if (parsedDraftData?.draftBenefSelected) {
        return !!form.getFieldsValue([key])[key] || claimDetailBackup[key];
      } else {
        return !!form.getFieldsValue([key])[key] && claimDetailBackup[key];
      }
    } else {
      if (!isBeneficiarySelected) {
        if (!form) {
          return;
        }
      }
      return !isDraftBtnClicked && !!form.getFieldsValue([key])[key] && claimDetailBackup[key];
    }
  };

  const onDesignationChange = (event: any): void => {
    const designation = getLookUpFullDetail([event], payerLookUp[uniqueIdentifier]?.designation || [], true);
    if (designation && !benefRoomType) {
      const { payerParentId } = designation[0];
      const selectedRoom = (payerLookUp[uniqueIdentifier]?.roomType || [])
        .find((ele: any): boolean => String(ele?.masterLookId) === String(payerParentId));
      if (selectedRoom) {
        form.setFieldsValue({ hospitalRoomType: OTHER_ID, payerRoomType: selectedRoom?.value });
        dispatch(toggleOtherRoomType(true));
        dispatch(disableRoomType(true));
      }
    }
  };

  const onChangeOtherMediClaimSwitch = (): void => {
    setIsOtherMediclaimRequired(!isOtherMediclaimRequired);
  };

  return formElementCount?.length ? <Panel title="Other Details">
    <Row gutter={GUTTOR_SPACE}>
      <FormTextInput detail={{
        ...commonDetail,
        key: 'certificateNumber',
        size: 12,
        required: isCertificateNoReq,
        disabled: isFormDisabled('certificateNumber'),
      }} />
      <FormSelect detail={{
        ...commonDetail,
        key: 'district',
        size: 12,
        options: (payerLookUp[uniqueIdentifier]?.district || []),
        showSearch: true,
        disabled: isFormDisabled('district'),
      }} />
      {adharHidden === 0 &&
        <FormCustomMaskedInput
          mask={'9999 9999 9999'}
          regxvalidator={AADHAR_VALIDATOR}
          detail={{
            ...commonDetail,
            key: 'aadharNo',
            size: 12,
            disabled: isFormDisabled('aadharNo') && isAadharValid,
            onFocus: (): void => handleUserFocus('aadharNo', setInputStates, true),
            onBlur: (): void => handleUserFocus('aadharNo', setInputStates, false),
            isShowSuggestedValue: inputStates.aadharNo.isShowSuggestedValue,
          }}
        />
      }
      <FormSelect detail={{
        ...commonDetail,
        key: 'designation',
        size: 12,
        options: (payerLookUp[uniqueIdentifier]?.designation || []),
        showSearch: true,
        disabled: isFormDisabled('designation'),
        onChange: onDesignationChange,
      }} />
      <FormTextInput detail={{
        ...commonDetail,
        key: 'relationTextInput',
        disabled: isFormDisabled('relationTextInput'),
        size: 12,
        onFocus: (): void => handleUserFocus('relationTextInput', setInputStates, true),
        onBlur: (): void => handleUserFocus('relationTextInput', setInputStates, false),
        isShowSuggestedValue: inputStates.relationTextInput.isShowSuggestedValue,
      }} />
      <FormSelect detail={{
        ...commonDetail,
        key: 'relation',
        size: 12,
        options: (payerLookUp[uniqueIdentifier]?.relation || []),
        showSearch: true,
        defaultValue: 'Self',
        disabled: (payerLookUp[uniqueIdentifier]?.relation)?.length === 1 ? true : isFormDisabled('relation'),
        onFocus: (): void => handleUserFocus('relation', setInputStates, true),
        onBlur: (): void => handleUserFocus('relation', setInputStates, false),
        isShowSuggestedValue: inputStates.relation.isShowSuggestedValue,
      }} />
      <FormTextInput detail={{
        ...commonDetail,
        key: 'memberEmergencyContactPerson',
        size: 12,
        onFocus: (): void => handleUserFocus('memberEmergencyContactPerson', setInputStates, true),
        onBlur: (): void => handleUserFocus('memberEmergencyContactPerson', setInputStates, false),
        isShowSuggestedValue: inputStates.memberEmergencyContactPerson.isShowSuggestedValue,
      }} />
      <FormTextInput detail={{
        ...commonDetail,
        key: 'unitName',
        size: 12,
        disabled: isFormDisabled('unitName'),
      }} />

      {/* extra formTextInput removed for unit name as causing a sanity bug - IPP - 3980 */}
      {/* <FormTextInput detail={{
        ...commonDetail,
        key: 'unitName',
        size: 12,
        disabled: isFormDisabled('unitName'),
      }} /> */}

      <FormSelect detail={{
        ...commonDetail,
        key: 'unitNameDropdown',
        size: 12,
        options: (payerLookUp[uniqueIdentifier]?.unitName || []),
        showSearch: true,
        disabled: isFormDisabled('unitNameDropdown'),
      }} />
      <FormTextInput detail={{
        ...commonDetail,
        key: 'employeeName',
        size: 12,
        disabled: isFormDisabled('employeeName'),
      }} />
      <FormTextInput detail={{
        ...commonDetail,
        key: 'placeOfPosting',
        size: 12,
        disabled: isFormDisabled('placeOfPosting'),
      }} />
      <FormTextInput detail={{
        ...commonDetail,
        key: 'familyPhysicianName',
        size: 12,
        disabled: isFormDisabled('familyPhysicianName'),
      }} />
      <FormInputMobile detail={{
        ...commonDetail,
        key: 'physicianMobileNumber',
        size: 12,
      }} />
      <FormInputMobile detail={{
        ...commonDetail,
        key: 'attendingRelativeMobile',
        size: 12,
      }} />
      <FormSwitch detail={{
        ...commonDetail,
        key: 'mediclaimOthers',
        size: 12,
        onChange: onChangeOtherMediClaimSwitch,
        disabled: isFormDisabled('mediclaimOthers'),
      }} />
      <FormTextInput detail={{
        ...commonDetail,
        key: 'otherMediclaim',
        size: 12,
        disabled: isFormDisabled('otherMediclaim'),
        required: isOtherMediclaimRequired,
      }}
      />
    </Row>
  </Panel> : null;
};

export default withRouter(OtherMemberDetailCard);
